import { isErrorResponse } from '@droplet-tech-code/core-elements/module/utils/error';
import { CreateVoucherRequest, VoucherListResponse } from '@naus-code/naus-admin-types';
import { useCallback } from 'react';

import { useFormModal } from '~/components/Modals/FormModal';
import { useAppNavigation } from '~/hooks/nav.hook';
import { voucherApi } from '~/screens/Vouchers/voucherApi';
import { translate } from '~/utils/translation.utils';

export const useCreateVoucherModal = () => {
  const navigate = useAppNavigation();
  const { open: openFormModal } = useFormModal();

  const useFormHookHandler = useCallback(() => {
    const { data: config, isLoading } = voucherApi.useCreateVoucherFormConfigQuery();
    return { data: { config }, isLoading };
  }, []);

  const onConfirm = async ({
    action,
    value,
  }: {
    action: (req: CreateVoucherRequest) => { data: VoucherListResponse };
    value: CreateVoucherRequest['voucher'];
  }) => {
    const response = await action({
      voucher: { ...value, value: value.value * 100 },
    });

    // On failure there is no voucher list to navigate to - leave the modal to
    // report the error rather than pushing a screen with no data.
    if (isErrorResponse(response)) {
      return response;
    }

    const vouchers = response.data.vouchers;
    const [firstVoucher] = vouchers;

    // A single voucher goes straight to its own page. A batch (qty / emails) has
    // many tiny ids, so the created list stays the only place that shows them all.
    if (vouchers.length === 1 && firstVoucher) {
      navigate.navigate('VoucherByTinyId', { voucherTinyId: firstVoucher.title });
    } else {
      navigate.navigate('CreateVoucher', { voucherList: response.data });
    }

    return response;
  };

  return {
    open: () => {
      openFormModal({
        modalTitle: translate('voucher.createVoucher'),
        useFormHookHandler,
        actionHook: voucherApi.useCreateVoucherMutation,
        onConfirm,
      });
    },
  };
};
